import { idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Scout } from '../types.ts';
import { damageHero, ev, isFlying } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The collapse (La Quemadura 1, docs/QUEMADURA.md): the glass at the Burn's edge breaks from one side of the field and
 * the break walks across it. The lost patrol lies wounded on the glass: a standing hero who stays by one lifts them,
 * and they follow that hero to the firm rock on the far side. Whatever is still on the glass when the break reaches it
 * goes down with it: nightmares, and the scouts left lying. A hero scrambles back off it, burnt. There is no node to
 * hold: the nightmares hunt the heroes.
 */

/** How far along the break's way a point lies (its axis is the unit vector `ax, ay` in bp). */
const along = (x: number, y: number, ax: number, ay: number): number => idiv(x * ax + y * ay, 10000);

export const collapseTick = (s: BattleState): void => {
  const r = s.rules?.collapse;
  if (!r) return;
  const st = (s.collapse ??= { f: r.from, saved: 0, lost: 0, fell: 0, people: r.spots.map((p, i) => ({ id: i + 1, x: p.x, y: p.y, hp: r.hp, maxHp: r.hp, pid: '', held: 0, facing: 0, moving: false, safe: false, at: p.at })) });
  if (!s.started) return;
  // The break walks on after its first wait, as far as the firm rock.
  if (s.waveT >= r.first && st.f > r.rock) {
    st.f = imax(r.rock, st.f - r.speed);
    if (s.tick % 6 === 0) ev(s, { t: 'collapse', kind: 'crack', x: 0, y: 0 });
  }
  // Nightmares on the broken glass go down with it.
  for (const e of s.enemies) {
    if (e.hp <= 0 || isFlying(e) || along(e.x, e.y, r.ax, r.ay) <= st.f) continue;
    e.hp = 0;
    st.fell++;
    ev(s, { t: 'collapse', kind: 'fall', x: e.x, y: e.y });
  }
  // A hero on it scrambles back toward the rock, burnt.
  for (const h of s.heroes) {
    if (h.downTicks > 0 || along(h.x, h.y, r.ax, r.ay) <= st.f) continue;
    h.shove = imax(h.shove ?? 0, 2);
    h.shoveX = -idiv(r.ax * r.shove, 10000);
    h.shoveY = -idiv(r.ay * r.shove, 10000);
    if (s.tick % 12 === 0) damageHero(s, h, imax(1, mulBp(h.stats.maxHp, r.burnBp)), null);
  }
  const standing = s.heroes.filter((h) => h.downTicks === 0);
  // In a line behind their hero, one step apart.
  const behind = new Map<string, number>();
  for (const p of st.people) {
    if (p.safe || p.hp <= 0 || s.waveT < p.at) continue;
    // The rest of the patrol stumbles out of the haze, one now and then.
    if (p.at > 0 && s.waveT === p.at) ev(s, { t: 'collapse', kind: 'found', x: p.x, y: p.y });
    const at = along(p.x, p.y, r.ax, r.ay);
    if (at > st.f) {
      p.hp = 0;
      st.lost++;
      ev(s, { t: 'collapse', kind: 'lost', x: p.x, y: p.y });
      continue;
    }
    if (at <= r.rock) {
      p.safe = true;
      p.moving = false;
      st.saved++;
      ev(s, { t: 'collapse', kind: 'safe', x: p.x, y: p.y });
      continue;
    }
    // Nightmares close by hurt them, each one now and then.
    if (s.tick % r.hurt.every === 0) for (const e of s.enemies) if (e.hp > 0 && e.kind !== 'boneWall' && withinRange(e.x, e.y, p.x, p.y, r.hurt.range)) p.hp -= r.hurt.damage;
    if (p.hp <= 0) {
      st.lost++;
      ev(s, { t: 'collapse', kind: 'lost', x: p.x, y: p.y });
      continue;
    }
    if (!p.pid) {
      // Lying on the glass: a hero who stays by them lifts them.
      const by = standing.find((h) => withinRange(h.x, h.y, p.x, p.y, r.lift));
      p.held = by ? p.held + 1 : 0;
      if (by && p.held >= r.ticks) {
        p.pid = by.pid;
        ev(s, { t: 'collapse', kind: 'lift', x: p.x, y: p.y });
      }
      continue;
    }
    lead(p, standing, behind, r.follow, r.walk);
  }
};

/** After their hero (or the nearest standing one if theirs fell), a step behind. */
const lead = (p: Scout, standing: BattleState['heroes'], behind: Map<string, number>, follow: number, walk: number): void => {
  const h = standing.find((x) => x.pid === p.pid) ?? standing[0];
  const n = h ? (behind.get(h.pid) ?? 0) : 0;
  if (h) behind.set(h.pid, n + 1);
  p.moving = !!h && !withinRange(h.x, h.y, p.x, p.y, follow + n * follow);
  if (h && p.moving) stepToward(p, h.x, h.y, walk);
};

/** Every scout on the rock or lost: won with enough of them safe; lost as soon as too few are left to make it. */
export const collapseResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.collapse;
  const st = s.collapse;
  if (!r || !st) return null;
  const left = st.people.length - st.saved - st.lost;
  if (st.saved + left < r.need) return 'lost';
  return left === 0 ? 'won' : null;
};
